import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { FilterDropdown } from "/imports/ui/components/shared";
import { SECTION_LABEL_SX, CARD_SX, AssignedResourcePanel, ALL_DURATIONS } from "/imports/ui/components/booking";

function LessonFilterSidebar({ lesson }) {
  const {
    currency,
    filters,
    setFilter,
    setFilters,
    availableSports,
    sportFilteredServices,
    trainerOptions,
    allowTrainerPick,
    durationOptions,
    serviceLocations,
    canApplyPreferences,
    applyPreferences,
    handleSportChange
  } = lesson;

  // Pack mode: sport/service/duration are pinned by the variant — show what's
  // being composed instead of dropdowns the pin effect would immediately revert.
  // Reschedule mode pins them to the lesson being moved.
  const packMode = lesson.pack?.active;
  const isRescheduling = !!lesson.reschedule?.active;
  const pinned = packMode || isRescheduling;
  const showLocation = serviceLocations.length > 0 && (isRescheduling ? serviceLocations.length > 1 : !packMode || !lesson.pack.packLocationId);

  return (
    <Box sx={{ width: "17.5rem", flexShrink: 0, position: "sticky", top: 88 }}>
      <Paper elevation={0} sx={{ ...CARD_SX, p: 2.5, display: "flex", flexDirection: "column", gap: 2.25 }}>
        {packMode && lesson.pack.resolved && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Booking package</Typography>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>{lesson.pack.pkg.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {lesson.pack.opt.sessions} × {lesson.pack.opt.sessionDuration} min · {lesson.pack.opt.linkedServiceName || ""}
            </Typography>
          </Box>
        )}

        {!pinned && canApplyPreferences && (
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<AutoAwesomeIcon sx={{ fontSize: "1rem" }} />}
            onClick={applyPreferences}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            Use my preferences
          </Button>
        )}

        {!pinned && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Sport</Typography>
            <FilterDropdown
              options={availableSports.map((s) => ({ value: s._id, label: s.name }))}
              value={filters.sport}
              onChange={(val) => handleSportChange(val)}
              allowAll={false}
              active={!!filters.sport}
              fullWidth
            />
          </Box>
        )}

        {!pinned && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Service</Typography>
            <FilterDropdown
              options={sportFilteredServices.map((s) => ({ value: s._id, label: s.name }))}
              value={filters.serviceId}
              onChange={(val) => setFilters({ serviceId: val, trainerId: null, duration: "" })}
              allowAll={false}
              active={!!filters.serviceId}
              fullWidth
            />
          </Box>
        )}

        {showLocation && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Location</Typography>
            <FilterDropdown
              options={serviceLocations.map((l) => ({ value: l._id, label: l.name }))}
              value={filters.locationId}
              onChange={(val) => setFilter("locationId", val)}
              allowAll={serviceLocations.length > 1}
              allLabel="All Locations"
              active={!!filters.locationId}
              fullWidth
            />
          </Box>
        )}

        {!pinned && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Duration</Typography>
            <FilterDropdown
              options={[
                // Several lengths: "All" lists every time and defers the length
                // to the picker — a start hidden for one length still shows.
                ...(durationOptions.length > 1 ? [{ value: ALL_DURATIONS, label: "All durations" }] : []),
                ...durationOptions.map((o) => ({
                  value: String(o.value),
                  label: o.price ? `${o.value} min — ${o.isFrom ? "from " : ""}${(currency || "").toUpperCase()} ${o.price}` : `${o.value} min`
                }))
              ]}
              value={filters.duration}
              onChange={(val) => setFilter("duration", val)}
              allowAll={false}
              active={!!filters.duration}
              fullWidth
            />
          </Box>
        )}

        <Box>
          <Typography sx={SECTION_LABEL_SX}>Trainer</Typography>
          {allowTrainerPick ? (
            <FilterDropdown
              options={trainerOptions.map((t) => ({ value: t._id, label: t.name }))}
              // A coach with no times on the day just loaded is reset to "Any Trainer" by an
              // effect; until it runs, the field reads as "Any" rather than holding a value
              // the list no longer has.
              value={trainerOptions.some((t) => t._id === filters.trainerId) ? filters.trainerId : null}
              onChange={(val) => setFilter("trainerId", val)}
              allLabel="Any Trainer"
              active={!!filters.trainerId}
              fullWidth
            />
          ) : (
            <AssignedResourcePanel noun="Trainer" names={trainerOptions.map((t) => t.name)} />
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default LessonFilterSidebar;
